/**
 * Notifications API Service
 * Handles notification settings, inbox, and read status
 */

import { api } from './client';
import { ENDPOINTS } from '../../config/api.config';
import logger from '../../utils/logger';

export async function fetchNotificationSettings() {
  logger.info('[Notifications API] Fetching settings');
  return api.get(ENDPOINTS.notifications.settings);
}

export async function updateNotificationSettings(settings) {
  logger.info('[Notifications API] Updating settings');
  return api.put(ENDPOINTS.notifications.settings, settings);
}

/**
 * Fetch notification inbox (hierarchy messages)
 * @param {Object} params - { limit, offset, unreadOnly }
 * @returns {Promise<Object>} { messages, unreadCount, pagination }
 */
export async function fetchNotificationInbox(params = {}) {
  logger.info('[Notifications API] Fetching inbox', { params });
  try {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `${ENDPOINTS.notifications.inbox}${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(endpoint);
    return response?.data || response;
  } catch (error) {
    logger.error('[Notifications API] Failed to fetch inbox', { error: error.message });
    throw error;
  }
}

/**
 * Fetch unread notification count
 * @returns {Promise<Object>} { unreadCount }
 */
export async function fetchUnreadCount() {
  try {
    const response = await api.get(ENDPOINTS.notifications.unreadCount);
    return response?.data || response;
  } catch (error) {
    logger.error('[Notifications API] Failed to fetch unread count', { error: error.message });
    throw error;
  }
}

/**
 * Mark a notification/message as read
 * @param {string} messageId - Message ID to mark as read
 * @returns {Promise<Object>} { messageId, readAt }
 */
export async function markNotificationRead(messageId) {
  logger.info('[Notifications API] Marking notification as read', { messageId });
  try {
    const response = await api.patch(ENDPOINTS.notifications.markRead(messageId));
    return response?.data || response;
  } catch (error) {
    logger.error('[Notifications API] Failed to mark as read', { messageId, error: error.message });
    throw error;
  }
}

export default {
  fetchNotificationSettings,
  updateNotificationSettings,
  fetchNotificationInbox,
  fetchUnreadCount,
  markNotificationRead,
};
